import { Building2, LogOut, UserRound } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { useBootstrap } from "../api/queries/useBootstrap";
import { signOut, useSession } from "../api/session";
import { SitePicker } from "../components/common/SitePicker";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useUiStore } from "../store/uiStore";
import { PlatformOverrideMenu } from "./PlatformOverrideMenu";

const APP_VERSION = import.meta.env["VITE_APP_VERSION"] ?? "dev";

export interface AccountMenuProps {
  /** Phone mounts the same menu inside the More sheet. */
  align?: "start" | "end";
}

export function AccountMenu({ align = "end" }: AccountMenuProps) {
  const { data } = useBootstrap();
  const mySiteId = useUiStore((s) => s.mySiteId);
  const setMySiteId = useUiStore((s) => s.setMySiteId);
  const { account } = useSession();
  const navigate = useNavigate();
  const user = data?.user;

  /* The signed-in account as the identity provider named it, which is there before the
     bootstrap answers; the bootstrap's own fields are the fallback. All are nullable and never
     render raw. */
  const name = account?.name ?? user?.displayName ?? "Signed in";
  const email = account?.email ?? user?.email ?? null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Account">
          <UserRound className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-64">
        <DropdownMenuLabel>
          <div className="type-body text-foreground">{name}</div>
          {email ? (
            <div className="type-caption font-normal text-muted-foreground">{email}</div>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <PlatformOverrideMenu />
        {/* My site is DEVICE-LOCAL (SPA-D11): never sent to the API, never a query key, never a
            URL parameter. `onSelect` is prevented so choosing inside the picker keeps the menu open. */}
        <div className="flex flex-col gap-2xs px-2 py-1.5" onKeyDown={(e) => e.stopPropagation()}>
          <span className="type-caption text-muted-foreground">My site</span>
          <SitePicker value={mySiteId} onChange={(next) => setMySiteId(next)} allowClear />
        </div>
        <DropdownMenuSeparator />
        {/* Switching tenants should not cost a sign-out (F-D3): the token is the same, only the
            `X-LL-Tenant` header changes, and the picker re-reads `GET /me/tenants` to show what
            this principal may actually select. */}
        <DropdownMenuItem onSelect={() => void navigate({ to: "/select-tenant" })}>
          <Building2 className="size-4" />
          Switch tenant
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="type-caption font-normal text-muted-foreground">
          Version {APP_VERSION}
        </DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
